import makeStyles from '@mui/styles/makeStyles'
import { Drawer, IconButton } from '@mui/material'
import MenuRoundedIcon from '@mui/icons-material/MenuRounded'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { NavBar } from './NavBar'
import { isClientUser } from '../../services/keycloak.service'
import { useHistory, useParams } from 'react-router-dom'
import { Pages } from '../../router/router'
import { useCurrentPage } from '../../hooks/useCurrentPage'
import { OrdersByStateCount } from '../../redux/reducers/orders'
import { useSelector } from '../../redux/hooks/useSelector'
import ClientCard from './ClientCard'
import { ClientInformationDialog } from '../dialogs/ClientInformationDialog'
import { Territory } from '../../redux/reducers/territories'
import { useUserApi } from '../../hooks/useUserApi'
import { updateUserTerritoryRequest } from '../../api/userApi'
import { AlertMessageConfirmation } from '../dialogs/AlertMessageConfirmation'
import { isApexUser } from '../../utils/apex'
import { tokens } from '../../styles/tokens'
import useMobileWidth from '../../hooks/useMobileWidth'

interface Props {
  pageName: string
}

const useStyles = makeStyles(theme => ({
  wrapper: {
    padding: theme.spacing(2),
    display: 'flex',
    maxWidth: '100%',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(1),
    },
  },
  leftBar: {
    width: '284px',
    position: 'fixed',
    top: theme.spacing(2),
    left: theme.spacing(2),
    bottom: theme.spacing(2),
    zIndex: 1,
  },
  drawerPaper: {
    width: '284px',
    maxWidth: '80vw',
    boxSizing: 'border-box',
    padding: theme.spacing(2),
    border: 'none',
  },
  right: {
    flexGrow: 1,
    minWidth: 0,
  },
  topBar: {
    display: 'flex',
    alignItems: 'stretch',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    marginLeft: '300px',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      flexWrap: 'wrap',
    },
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch',
    },
  },
  titleGroup: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      flexWrap: 'wrap',
    },
  },
  metaGroup: {
    marginLeft: 'auto',
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0,
      flexWrap: 'wrap',
    },
  },
  child: {
    marginLeft: '300px',
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
    },
  },
  menuButton: {
    marginRight: theme.spacing(1),
  },
  pageName: {
    fontWeight: 400,
    fontSize: '1.6rem',
    letterSpacing: '-0.01em',
    color: tokens.color.neutral[900],
    margin: 0,

    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    height: '85px',
    [theme.breakpoints.down('sm')]: {
      fontSize: '1.2rem',
      padding: theme.spacing(2),
      height: 'auto',
      minHeight: '56px',
    },
  },
  titleRibon: {
    fontWeight: 500,
    fontSize: '1rem',
    margin: 0,
    textAlign: 'center'
  },
  rightTop: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}))

interface NewLayoutProps {
  setTerritoryChange?: (param: Territory) => any
  pageName: string
  hideClientCard?: boolean
}


export const NewLayout: React.FC<NewLayoutProps> = ({ children, pageName, setTerritoryChange, hideClientCard }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const isMobile = useMobileWidth()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const curr = useCurrentPage()
  const [ordersByStateCount, setOrdersByStateCount] = useState([] as OrdersByStateCount[])
  const { clientId } = useParams<{ clientId: string }>()
  const orderSse = useSelector(state => state.orders.orderSse)
  const user = useSelector(state => state.auth.user)
  const { updateUserTerritorySync, getUser } = useUserApi()
  const history = useHistory()
  const [activeTerritory, setActiveTerritory] = useState<Territory>();
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [openConfirmationSave, setOpenConfirmationSave] = useState<boolean>(false);
  const clientNameFromEmail = useSelector(state => state.clients.clientName);


  useEffect(() => {
    if (curr === Pages.Home && orderSse && orderSse.userStates && orderSse.userStates.length > 0) {
      setOrdersByStateCount(orderSse.userStates[0].states)
    }
  })

  useEffect(()=>{
    if ( activeTerritory && user && activeTerritory.businessTerritoryId === user.businessTerritoryId ) {
      setTerritoryChange?.(activeTerritory)
      history.push(Pages.Home)
    }
  }, [user])

  const hadleTerritoryChange = (item: Territory) => {
    if (isClientUser()) {
      // Klientský účet: nový clientId claim se do tokenu dostane až po plném
      // reloadu (re-auth), refresh v updateUserTerritorySync nestačí.
      updateUserTerritoryRequest(item.businessTerritoryId)
        .then(() => {
          window.location.assign(process.env.PUBLIC_URL || '/')
        })
        .catch(error => {
          console.error(error)
        })
      return
    }
    setActiveTerritory(item);
    updateUserTerritorySync(item.businessTerritoryId, user.kcId)
  }

  const handleConfirmation2 = () => {
    setOpenConfirmation(false)
    setOpenConfirmationSave(true)
  }
  const handleCloseConfirmation2 = () => {
    setOpenConfirmation(false)
  }

  const ribon = process.env.REACT_APP_ENV_RIBBON
  return (
    <div className={classes.wrapper}>
      {isMobile ? (
        <Drawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          classes={{ paper: classes.drawerPaper }}
        >
          <NavBar onTerritoryChange={hadleTerritoryChange} />
        </Drawer>
      ) : (
        <div className={classes.leftBar}>
          <NavBar onTerritoryChange={hadleTerritoryChange} />
        </div>
      )}
      <div className={classes.right}>
        <div className={classes.topBar}>
          <span className={classes.titleGroup}>
            {isMobile && (
              <IconButton
                className={classes.menuButton}
                aria-label="menu"
                onClick={() => setDrawerOpen(true)}
              >
                <MenuRoundedIcon />
              </IconButton>
            )}
            <p className={classes.pageName}>{t(pageName)}</p>
            {!isClientUser() && clientId && !hideClientCard && <ClientCard />}
          </span>
          <span className={classes.metaGroup}>
            {ribon && (
              <p
                className={classes.titleRibon}
                // style={!isClientUser()?{marginLeft: '-200px', color: '#ff0000'}:{color: '#ff0000'}}
                style={{ color: tokens.color.error[500] }}
              >
                {ribon}
              </p>
            )}
          </span>
        </div>
        <div className={classes.child}>{children}</div>
      </div>
      <ClientInformationDialog
        setOpenConfirmation={setOpenConfirmation}
        openConfirmationSave={openConfirmationSave}
        setOpenConfirmationSave={setOpenConfirmationSave}
      />
      <AlertMessageConfirmation
        alertOpen={openConfirmation}
        onOk={handleConfirmation2}
        onCancle={handleCloseConfirmation2}
        alertText={t('DUPLICITY_EMAIL_VALIDATION_TEXT', {name: clientNameFromEmail.name})}
      />

    </div>
  )
}
